import { api } from "../api_middleware";
import { getEffectiveBaseUrl } from "../utils/serverUrls";

export interface UserName {
  first_name?: string | null;
  first_name_phonetic?: string | null;
  middle_name?: string | null;
  middle_name_phonetic?: string | null;
  last_name?: string | null;
  last_name_phonetic?: string | null;
}

export interface CreatePixelsUserRequest {
  name: UserName;
  email?: string | null;
  linkedin_url: string;
  organization_id?: string | null;
}

export enum Environment {
  PRODUCTION = "production",
  DEMO = "demo",
  STAGING = "staging",
  QA = "qa",
  TEST = "test",
  DEV = "dev",
  UNKNOWN = "unknown",
}

export enum ConversationalMode {
  COMPOSER = "COMPOSER",
  AUDIO_ONLY = "AUDIO_ONLY",
}

export interface APIConversation {
  id: string;
  is_private: boolean;
  environment: Environment;
  supported_modes?: ConversationalMode[];
}

export enum UserStatus {
  ACTIVE = "active",
  WAITLISTED = "waitlisted",
  SUSPENDED = "suspended",
  BANNED = "banned",
  ONBOARDING = "onboarding",
}

export enum PixelStreamingExperienceDisabledClass {
  LINK_CONSUMPTION_EXHAUSTED = "LINK_CONSUMPTION_EXHAUSTED",
}

export interface PixelStreamingExperienceDisabledRationale {
  disabled_class: PixelStreamingExperienceDisabledClass;
  error_text?: string | null;
}

export interface PixelStreamingExperience {
  is_enabled: boolean;
  disabled_rationale?: PixelStreamingExperienceDisabledRationale | null;
  organization_id: string;
  res_link_params_id?: string | null;
  linkedin_url_requested: boolean;
  linkedin_url_required: boolean;
  logout_user_post_experience: boolean;
  supports_relaunch_post_experience: boolean;
  bot_name: string;
  requests_feedback_post_experience: boolean;
}

export interface APIUser {
  id: string;
  is_admin: boolean;
  is_waitlisted: boolean;
  status: UserStatus;
  conversations: APIConversation[];
  consented_to_latest_tos: boolean;
  consented_to_any_tos: boolean;
  phone_number?: string | null;
  email?: string | null;
  full_name?: string | null;
  name?: UserName | null;
  is_active?: boolean;
  is_developer?: boolean;
  environment?: Environment;
  ad_source?: string | null;
  is_guest?: boolean;
  organization_id?: string | null;
  supported_pixel_streaming_experiences?: PixelStreamingExperience[];
  user_scopes?: string[];
}

export interface CreatePixelsUserResponse {
  user: APIUser;
}

interface FinishOnboardingPixelsUserRequest {
  name: UserName;
  linkedin_url?: string;
  res_link_params_id?: string;
  organization_id?: string;
}

interface FinishOnboardingPixelsUserResponse {
  user: APIUser;
}

export const createLinkedInUser = async (
  linkedinUrl: string,
  firstName: string,
  middleName?: string,
  lastName?: string,
  email?: string,
  createTokenJwt?: string,
  resLinkParamsId?: string
): Promise<APIUser> => {
  const name: Record<string, string> = { first_name: firstName };
  if (middleName !== undefined) {
    name.middle_name = middleName;
  }
  if (lastName !== undefined) {
    name.last_name = lastName;
  }
  const createUserRequest = {
    name: name,
    email: email,
    linkedin_url: linkedinUrl,
    create_token_jwt: createTokenJwt,
    res_link_params_id: resLinkParamsId,
  };
  console.log("createLinkedInUser request:", createUserRequest);
  const createUserResponse = await api(
    "v1/pixels/users/create-user",
    "POST",
    getEffectiveBaseUrl(),
    createUserRequest
  );
  console.log("createLinkedInUser response:", createUserResponse);
  return createUserResponse.user as APIUser;
};

export const createUser = async (
  organizationId?: string,
  email?: string,
  createTokenJwt?: string,
  res_link_params_id?: string,
): Promise<APIUser> => {
  const createUserRequest = {
    email: email,
    create_token_jwt: createTokenJwt,
    res_link_params_id: res_link_params_id,
    is_deferred_onboarding_flow: true,
    organization_id: organizationId,
  };
  console.log("createUser request:", createUserRequest);
  const createUserResponse = await api(
    "v1/pixels/users/create-user",
    "POST",
    getEffectiveBaseUrl(),
    createUserRequest
  );
  console.log("createUser response:", createUserResponse);
  return createUserResponse.user as APIUser;
};

export const finishOnboardingUser = async (
  firstName?: string,
  middleName?: string,
  lastName?: string,
  linkedInURL?: string,
  resLinkParamsId?: string,
  organizationId?: string,
): Promise<APIUser> => {
  const finishOnboardingUserRequest: FinishOnboardingPixelsUserRequest = {
    name: { first_name: firstName, middle_name: middleName, last_name: lastName },
    linkedin_url: linkedInURL,
    res_link_params_id: resLinkParamsId,
    organization_id: organizationId,
  };
  console.log("finishOnboardingUserRequest request:", finishOnboardingUserRequest);
  const finishOnboardingUserResponse = await api(
    "v1/pixels/users/finish-onboarding-user",
    "POST",
    getEffectiveBaseUrl(),
    finishOnboardingUserRequest
  ) as FinishOnboardingPixelsUserResponse;
  console.log("finishOnboardingUser response:", finishOnboardingUserResponse);
  return finishOnboardingUserResponse.user as APIUser;
};

type BuildOpts = {
  resLinkParamsId?: string | number | null;
  organizationId?: string | number | null;
};

function buildQuery(
  orgParamName: string,
  resLinkParamsId?: string,
  organizationId?: string,
): string {
  const params: string[] = [];

  if (resLinkParamsId) {
    params.push(`res_link_params_id=${encodeURIComponent(resLinkParamsId)}`);
  }
  if (organizationId) {
    params.push(`${orgParamName}=${encodeURIComponent(organizationId)}`);
  }

  return params.length ? `?${params.join("&")}` : "";
}

export const getUser = async (userId: string, resLinkParamsId?: string, organizationId?: string): Promise<APIUser> => {
  // Will throw if no user
  console.log("getuser:", userId, "resLinkParamsId:", resLinkParamsId, "organizationId:", organizationId);
  const getUserResponse = await api(
    "v1/pixels/users/" + userId + buildQuery("org_id", resLinkParamsId, organizationId),
    "GET",
    getEffectiveBaseUrl()
  );
  console.log("getUser response:", getUserResponse);
  return getUserResponse as APIUser;
};

export const getPartnerUser = async (resLinkParamsId?: string, organizationId?: string): Promise<APIUser> => {
  // Will throw if no user
  console.log("getPartnerUser resLinkParamsId:", resLinkParamsId, "organizationId:", organizationId);
  const getUserResponse = await api(
    "v1/pixels/partners/current-user" + buildQuery("organization_id", resLinkParamsId, organizationId),
    "GET",
    getEffectiveBaseUrl()
  );
  console.log("getPartnerUser response:", getUserResponse);
  return getUserResponse as APIUser;
};

export const getUserStatusErrorMessage = (userStatus: UserStatus): string => {
  switch (userStatus) {
    case UserStatus.WAITLISTED:
      return "You are waitlisted. Please check your email for updates.";
    case UserStatus.SUSPENDED:
      return "You have been suspended.";
    case UserStatus.BANNED:
      return "You have been banned.";
    case UserStatus.ONBOARDING:
      return "You are currently onboarding.";
    default:
      return "An unknown error occurred.";
  }
}
